import { firstValueFrom, throwError } from 'rxjs';
import { filter, map, tap, timeout } from 'rxjs/operators';
import { ModuleEventLevel, } from '../../../types.js';
import { RequiredModuleTimeoutError } from '../types.js';
import { ModuleConfiguratorEventName } from '../module-configurator-event-name.js';
/**
 * Creates a `requireInstance` resolver for use during module initialization.
 *
 * `requireInstance` is passed into each module's `initialize` call so it can
 * declare dependencies on other modules without knowing whether those modules
 * have already finished initializing. The resolver waits up to `wait` seconds
 * for the target module to appear in the shared `instance$` subject.
 *
 * @param moduleNames - Names of all modules registered in this initialization run.
 * @param instance$ - Shared subject accumulating initialized module instances.
 * @param registerEvent - Function to emit lifecycle events.
 * @returns A `requireInstance` function matching the shape expected by `ModuleInitializerArgs`.
 * @template T - The shape of the accumulated module instance map.
 * @throws {Error} When the requested module name was never registered.
 * @throws {RequiredModuleTimeoutError} When the requested module does not
 *   initialize within its timeout window.
 * @internal
 */
export function createRequireInstance(moduleNames, instance$, registerEvent) {
    // The implementation signature is concrete (name: string) but the public contract
    // must be the generic shape expected by ModuleInitializerArgs. Module names are
    // always strings at runtime — the keyof-derived `string | number` constraint comes
    // from TypeScript's keyof semantics, not actual usage.
    return function requireInstance(name, wait = 60) {
        // Fail fast if the caller requests a module that was never registered —
        // this almost always indicates a misconfiguration rather than a timing issue.
        if (!moduleNames.includes(name)) {
            const error = new Error(`Cannot require [${String(name)}] since module is not defined!`);
            error.name = 'ModuleNotDefinedError';
            registerEvent({
                level: ModuleEventLevel.Error,
                name: ModuleConfiguratorEventName.RequireInstanceModuleNotDefined,
                message: error.message,
                properties: { moduleName: String(name), wait },
                error,
            });
            throw error;
        }
        // Short-circuit: module is already in the accumulated instance object
        if (instance$.value[name]) {
            registerEvent({
                level: ModuleEventLevel.Debug,
                name: ModuleConfiguratorEventName.RequireInstanceModuleAlreadyInitialized,
                message: `Module [${String(name)}] is already initialized, skipping queue`,
                properties: { moduleName: String(name), wait },
            });
            return Promise.resolve(instance$.value[name]);
        }
        const requireStart = performance.now();
        registerEvent({
            level: ModuleEventLevel.Debug,
            name: ModuleConfiguratorEventName.RequireInstanceAwaitingModule,
            message: `Awaiting module [${String(name)}] initialization, timeout ${wait}s`,
            properties: { moduleName: String(name), wait },
        });
        // Wait for the module to appear in the shared instance subject, up to `wait` seconds.
        return firstValueFrom(instance$.pipe(
        // Ignore emissions until the requested module has been added to the instance map.
        filter((x) => !!x[name]), 
        // Resolve the dependency promise with the provider instance, not the full module map.
        map((x) => x[name]), 
        // Convert unresolved dependencies into a typed timeout error with lifecycle diagnostics.
        timeout({
            // requireInstance accepts seconds; RxJS timeout expects milliseconds.
            each: wait * 1000,
            with: () => throwError(() => {
                const error = new RequiredModuleTimeoutError();
                registerEvent({
                    level: ModuleEventLevel.Error,
                    name: ModuleConfiguratorEventName.RequireInstanceTimeout,
                    message: `Module [${String(name)}] initialization timed out after ${wait}s`,
                    properties: { moduleName: String(name), wait },
                    error,
                });
                return error;
            }),
        }), 
        // Emit timing diagnostics only after the dependency has actually resolved.
        tap(() => {
            const requireTime = Math.round(performance.now() - requireStart);
            registerEvent({
                level: ModuleEventLevel.Debug,
                name: ModuleConfiguratorEventName.RequireInstanceModuleResolved,
                message: `Module [${String(name)}] required in ${requireTime}ms`,
                properties: { moduleName: String(name), wait, requireTime },
                metric: requireTime,
            });
        })));
    };
}
export default createRequireInstance;
//# sourceMappingURL=create-require-instance.js.map